import React from 'react';
/**
 * DjayVariantPicker — The "Kies je stijl" long-press popover that lets
 * the learner pick a Djay variant (and shows the DJ Goud unlock reveal
 * the first time it's discovered).
 *
 * Extracted from FloatingDjay.tsx (audit P1, ronde 2 2026). Pure
 * presentation — receives unlocked variants + selected variant +
 * change handler. The egg-label is driven by a parent flag because the
 * timing of its dismissal is owned by the parent's interaction state.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { LockIcon, SparklesIcon } from 'lucide-react';
import { DjayAvatar } from '../DjayAvatar';
import type { VariantUnlockInfo } from '../useVariantUnlocks';
import type { DjayVariant } from '../DjayAvatar';
interface DjayVariantPickerProps {
  isOpen: boolean;
  variants: VariantUnlockInfo[];
  selectedVariant: DjayVariant;
  shouldAnimate: boolean;
  showEasterEggLabel: boolean;
  onSelect: (variant: DjayVariant) => void;
}
export function DjayVariantPicker({
  isOpen,
  variants,
  selectedVariant,
  shouldAnimate,
  showEasterEggLabel,
  onSelect
}: DjayVariantPickerProps) {
  return <AnimatePresence data-id="element-2871">
      {isOpen && <motion.div initial={shouldAnimate ? {
      opacity: 0,
      y: 10,
      scale: 0.95
    } : false} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={shouldAnimate ? {
      opacity: 0,
      y: 10,
      scale: 0.95
    } : false} transition={{
      duration: 0.15,
      ease: [0.32, 0.72, 0, 1]
    }} className="absolute bottom-full right-0 mb-4 w-[280px] md:w-[340px] bg-white rounded-2xl shadow-xl border border-slate-100 p-4 origin-bottom-right pointer-events-auto" data-id="element-2872">
          <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-b border-r border-slate-100 transform rotate-45" data-id="element-2873" />

          <div className="relative z-10" data-id="element-2874">
            <p className="text-xs text-slate-400 font-medium mb-3" data-id="element-2875">
              Kies je stijl
            </p>

            <AnimatePresence data-id="element-2876">
              {showEasterEggLabel && <EasterEggLabel data-id="element-2877" />}
            </AnimatePresence>

            <div className="grid grid-cols-4 gap-2" data-id="element-2878">
              {variants.map(v => {
            const isSelected = selectedVariant === v.id;
            const isNewGold = showEasterEggLabel && v.id === 'goud';
            return <button key={v.id} onClick={() => {
              if (v.unlocked) onSelect(v.id);
            }} disabled={!v.unlocked} className="flex flex-col items-center gap-1.5 group focus:outline-none" title={v.unlocked ? v.name : `${v.name} (Vergrendeld)`} aria-label={`Kies ${v.name} stijl`} data-id="element-2879">
                    <motion.div animate={isNewGold ? {
                scale: [1, 1.15, 1]
              } : {}} transition={isNewGold ? {
                duration: 1,
                repeat: Infinity
              } : {}} className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 relative
                        ${isNewGold ? 'ring-2 ring-yellow-400 bg-yellow-50 shadow-lg shadow-yellow-200/50' : ''}
                        ${isSelected && !isNewGold ? 'ring-2 ring-orange-400 scale-105 bg-orange-50 shadow-sm' : ''}
                        ${!isSelected && !isNewGold ? 'bg-slate-50 border border-slate-100 shadow-sm group-hover:bg-slate-100' : ''}
                        ${!v.unlocked ? 'opacity-50 grayscale cursor-not-allowed' : 'cursor-pointer'}
                      `} data-id="element-2880">
                      <div className="w-10 h-10" data-id="element-2881">
                        <DjayAvatar variant={v.id} emotion="happy" size="sm" data-id="element-2882" />
                      </div>
                      {!v.unlocked && <div className="absolute inset-0 bg-slate-900/5 rounded-full flex items-center justify-center backdrop-blur-[0.5px]" data-id="element-2883">
                          <LockIcon className="w-4 h-4 text-slate-500 drop-shadow-sm" data-id="element-2884" />
                        </div>}
                    </motion.div>
                    <span className={`text-[10px] font-medium transition-colors leading-tight text-center
                        ${isNewGold ? 'text-yellow-600 font-bold' : ''}
                        ${isSelected && !isNewGold ? 'text-orange-600' : ''}
                        ${!isSelected && !isNewGold ? 'text-slate-500' : ''}
                      `} data-id="element-2885">
                      {v.name}
                    </span>
                  </button>;
          })}
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}
/** The "🏆 Geheim ontdekt!" banner that appears once when DJ Goud
 * unlocks for the first time. Kept inline because it's only used here. */
function EasterEggLabel() {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.8,
    y: -10
  }} animate={{
    opacity: 1,
    scale: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.8,
    y: -10
  }} transition={{
    type: 'spring',
    damping: 15,
    stiffness: 200
  }} className="mb-4 relative overflow-hidden rounded-xl border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 p-4" data-id="element-2886">
      <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-200/40 to-transparent" animate={{
      x: ['-100%', '200%']
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-2887" />
      <div className="relative z-10 flex items-center gap-3" data-id="element-2888">
        <motion.div animate={{
        rotate: [0, 15, -15, 0],
        scale: [1, 1.2, 1]
      }} transition={{
        duration: 0.6,
        repeat: 2
      }} data-id="element-2889">
          <SparklesIcon className="w-8 h-8 text-yellow-500" data-id="element-2890" />
        </motion.div>
        <div data-id="element-2891">
          <p className="font-black text-amber-900 text-base" data-id="element-2892">
            🏆 Geheim ontdekt!
          </p>
          <p className="text-sm font-bold text-amber-700" data-id="element-2893">
            DJ Goud is ontgrendeld!
          </p>
        </div>
      </div>
      {[...Array(6)].map((_, i) => <motion.div key={i} className="absolute w-1.5 h-1.5 bg-yellow-400 rounded-full" style={{
      left: `${15 + i * 15}%`,
      top: `${20 + i % 3 * 25}%`
    }} animate={{
      y: [0, -12, 0],
      opacity: [0.3, 1, 0.3],
      scale: [0.8, 1.2, 0.8]
    }} transition={{
      duration: 1.5,
      repeat: Infinity,
      delay: i * 0.2
    }} data-id="element-2894" />)}
    </motion.div>;
}